/* ============================================================
   <store-hours> — the live "Open now · closes 7pm" badge.

   Used on the Locations hub (sections/jays-locations.liquid) and the store
   pages (sections/jays-store.liquid). The element carries the same hours
   JSON the Liquid hours table is rendered from (the `hours` field on the
   `location` metaobject), so the badge and the table can't drift apart.

     <store-hours data-hours='{"mon":["10:00","19:00"],...}'
                  data-tz="America/Chicago"></store-hours>

   Renders one of:
     Open now · closes 7pm          (inside today's window)
     Opens 10am                     (today, before opening)
     Closed · opens 10am tomorrow   (after closing, or a closed day)
   The current time is read in the store's timezone via Intl, so a customer
   in another zone still sees the store's real state. If anything is missing
   or the browser can't do timezones, the element stays empty — the hours
   table next to it is always there as the fallback.
   ============================================================ */
(function () {
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  function fmt(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    const suffix = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return m ? `${h12}:${String(m).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
  }
  function mins(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  }

  // "Now" in the store's timezone as { day: 0-6, minutes since midnight }.
  function nowIn(tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false,
      }).formatToParts(new Date());
      const get = (t) => parts.find((p) => p.type === t)?.value;
      const day = DAYS.indexOf(get('weekday').toLowerCase().slice(0, 3));
      const hour = Number(get('hour')) % 24; // some engines report "24" at midnight
      return { day, minutes: hour * 60 + Number(get('minute')) };
    } catch (e) {
      return null;
    }
  }

  class StoreHours extends HTMLElement {
    connectedCallback() {
      this.render();
      // Re-check each minute so a page left open flips at closing time.
      this._timer = setInterval(() => this.render(), 60 * 1000);
    }
    disconnectedCallback() { clearInterval(this._timer); }

    render() {
      let hours;
      try { hours = JSON.parse(this.dataset.hours || 'null'); } catch (e) { hours = null; }
      const now = nowIn(this.dataset.tz || 'America/Chicago');
      if (!hours || !now) { this.textContent = ''; return; }

      const today = hours[DAYS[now.day]];
      let state = 'closed';
      let text = '';

      if (today && today.length === 2 && now.minutes >= mins(today[0]) && now.minutes < mins(today[1])) {
        state = 'open';
        const left = mins(today[1]) - now.minutes;
        // Under an hour to go: say so, it changes whether someone drives over.
        text = left <= 60 ? `Open now · closes in ${left} min` : `Open now · closes ${fmt(today[1])}`;
      } else if (today && today.length === 2 && now.minutes < mins(today[0])) {
        state = 'soon';
        text = `Opens ${fmt(today[0])} today`;
      } else {
        // Find the next day with hours (max one week out).
        for (let i = 1; i <= 7; i++) {
          const next = hours[DAYS[(now.day + i) % 7]];
          if (next && next.length === 2) {
            const when = i === 1 ? 'tomorrow' : DAYS[(now.day + i) % 7].replace(/^./, (c) => c.toUpperCase());
            text = `Closed · opens ${fmt(next[0])} ${when}`;
            break;
          }
        }
      }

      this.dataset.state = state;
      this.innerHTML = `<span class="jays-open__dot" aria-hidden="true"></span>${text}`;
      this.setAttribute('aria-label', text);
    }
  }

  if (!customElements.get('store-hours')) customElements.define('store-hours', StoreHours);
})();
